import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TradieProfilePage = () => {
  const { tradieId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tradie, setTradie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTradie();
  }, [tradieId]);

  const fetchTradie = async () => {
    try {
      const response = await axios.get(`${API}/tradies/${tradieId}`);
      setTradie(response.data);
    } catch (error) {
      console.error('Failed to fetch tradie:', error);
      toast.error('Tradie not found');
      navigate('/tradies/browse');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageTradie = async (tradieUserId) => {
    if (!user?.email_verified) {
      toast.error('Please verify your email first');
      return;
    }
    try {
      const response = await axios.post(`${API}/conversations?other_user_id=${tradieUserId}`);
      navigate(`/messages?conversation=${response.data.conversation_id}`);
    } catch (error) {
      toast.error('Failed to start conversation');
    }
  };

  const getTierBadge = (tier) => {
    const badges = {
      basic: <Badge variant="secondary" className="bg-gray-500 text-white">Basic</Badge>,
      premium: <Badge className="bg-blue-500 text-white">Premium</Badge>,
      pro: <Badge className="bg-yellow-500 text-white">Pro</Badge>,
    };
    return badges[tier] || badges.basic;
  };

  const getVerificationBadge = (status) => {
    const badges = {
      verified: <Badge className="bg-green-500 text-white text-xs">✓ Verified</Badge>,
      pending: <Badge variant="outline" className="text-yellow-600 text-xs">⏳ Pending</Badge>,
      rejected: <Badge variant="destructive" className="text-xs">✗ Rejected</Badge>,
      not_provided: <Badge variant="secondary" className="text-xs">Not Provided</Badge>,
      not_required: <Badge variant="secondary" className="text-xs">Not Required</Badge>,
    };
    return badges[status] || badges.not_provided;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-screen w-full" />
      </div>
    );
  }

  if (!tradie) return null;

  return (
    <div className="container mx-auto px-4 py-8" data-testid="tradie-profile-page">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Profile Header */}
        <Card>
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-accent-green/20 flex items-center justify-center flex-shrink-0">
                <span className="text-5xl">👷</span>
              </div>

              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold" data-testid="tradie-business-name">{tradie.business_name}</h1>
                    {getTierBadge(tradie.current_tier)}
                  </div>
                  <Badge variant="outline" className="capitalize">{tradie.tradie_type}</Badge>
                </div>

                <p className="text-muted-foreground">{tradie.bio || 'No bio provided'}</p>

                <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Experience:</span>
                    <span className="ml-2 font-medium">{tradie.years_experience} years</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Service areas:</span>
                    <span className="ml-2 font-medium">{tradie.service_areas?.slice(0, 3).join(', ')}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trust Block */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Verification Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-sm font-medium mb-1">ABN</div>
                {getVerificationBadge(tradie.abn_status)}
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Licence</div>
                {getVerificationBadge(tradie.licence_status)}
              </div>
              <div>
                <div className="text-sm font-medium mb-1">Insurance</div>
                {getVerificationBadge(tradie.insurance_status)}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <div>
          <h2 className="text-2xl font-bold mb-6">Our Services</h2>
          {tradie.services && tradie.services.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tradie.services.map((service) => (
                <Card
                  key={service.id}
                  className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1"
                  onClick={() => navigate(`/tradies/services/${service.id}`)}
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-primary/10 to-accent-green/10 rounded-t-xl" />
                  <CardContent className="p-4 space-y-2">
                    <h3 className="font-semibold line-clamp-2">{service.title}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-500">★</span>
                      <span className="text-sm">{service.rating_avg.toFixed(1)}</span>
                    </div>
                    <div className="text-lg font-bold">${service.price_base.toFixed(2)}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                No services listed yet
              </CardContent>
            </Card>
          )}
        </div>

        {/* Reviews */}
        {tradie.reviews && tradie.reviews.length > 0 && (
          <Card>
            <CardContent className="p-6 space-y-6">
              <h3 className="font-semibold text-lg">Customer Reviews</h3>
              <div className="space-y-4">
                {tradie.reviews.slice(0, 5).map((review) => (
                  <div key={review.id} className="border-l-2 border-primary/20 pl-4 py-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className={`text-sm ${i < review.rating ? 'text-yellow-500' : 'text-gray-300'}`}>★</span>
                        ))}
                      </div>
                      <span className="text-sm font-medium">{review.customer_name_masked}</span>
                      <span className="text-sm text-muted-foreground">{review.suburb}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{review.comment}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA Footer */}
        <Card className="gf-grad-accent">
          <CardContent className="p-8 text-center space-y-4">
            <h3 className="text-2xl font-bold">Ready to start your project?</h3>
            <p className="text-muted-foreground">Get in touch to discuss your requirements</p>
            <Button
              size="lg"
              className="rounded-xl bg-primary hover:bg-primary-hover shadow-lg"
              onClick={() => {
                if (!user) {
                  toast.error('Please login to message tradies');
                  navigate('/login');
                } else {
                  handleMessageTradie(tradie.user_id);
                }
              }}
            >
              Send Message
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TradieProfilePage;
